/**
 * UserContext — source unique de vérité pour l'utilisateur authentifié.
 *
 * WHY: useUser() était instancié 16+ fois, chacun avec son propre React state.
 * Les module-level guards (_globalEnrichInProgress) bloquaient tous sauf UNE instance
 * d'appeler /users/me. Les autres restaient avec les données Auth0-only (companyId=undefined).
 *
 * FIX: Un seul Provider appelle /users/me une fois. Tous les consommateurs (useUser())
 * lisent le même state via useContext — propagation React native, zéro guards nécessaires.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { User } from '../types/profile';
import { UserType } from '../types/common';
import { userService } from '../services/user';
import userApiService from '../services/userApi';
import cloudinaryService from '../services/cloudinaryService';
import { getToken } from '../utils/storage';
import { useToast } from '../hooks/useToast';
import { checkBackendHealth, getBackendHealthStatus } from '../utils/backendHealth';
import { ensureFreshToken } from '../utils/tokenRefresh';

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_RETRIES = 2;
const RETRY_DELAY = 3000;
const USER_TYPE_PREFERENCE_KEY = 'wanzo_user_type_preference';
const ENRICHED_PROFILE_KEY = 'wanzo_enriched_profile';

// ─── Exported helpers (re-used by AuthInitializer, Callback, useAuthErrorHandler) ──

/**
 * Type d'utilisateur préféré (optionnel — le backend le détermine selon le formulaire).
 */
function getPreferredUserType(): UserType | undefined {
  const storedPreference = localStorage.getItem(USER_TYPE_PREFERENCE_KEY);
  if (storedPreference === 'financial_institution' || storedPreference === 'sme') {
    console.log('📋 UserType récupéré depuis préférence stockée:', storedPreference);
    return storedPreference;
  }
  const currentPath = window.location.pathname;
  const redirectPath = sessionStorage.getItem('auth0_redirect_after_login') || '';
  if (currentPath.includes('financial-institution') || redirectPath.includes('financial-institution')) {
    console.log('🏦 UserType déduit de l\'URL:', 'financial_institution');
    return 'financial_institution';
  }
  console.log('📝 UserType non défini - sera déterminé par le backend selon le formulaire');
  return undefined;
}

export function setPreferredUserType(userType: UserType): void {
  localStorage.setItem(USER_TYPE_PREFERENCE_KEY, userType);
  console.log('✅ Préférence userType enregistrée:', userType);
}

export function clearPreferredUserType(): void {
  localStorage.removeItem(USER_TYPE_PREFERENCE_KEY);
  console.log('🗑️ Préférence userType effacée');
}

export const AUTH_EVENT = 'auth_state_changed';

let _notifyDebounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Déclenche AUTH_EVENT avec debounce 200ms (cross-tab sync et callbacks Auth0). */
export function notifyAuthChange() {
  if (_notifyDebounceTimer) clearTimeout(_notifyDebounceTimer);
  _notifyDebounceTimer = setTimeout(() => {
    _notifyDebounceTimer = null;
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
  }, 200);
}

/** Cache du profil enrichi pour la persistence localStorage (cross-tab). */
let _persistedEnrichedUser: User | null = null;

function persistEnrichedProfile(user: User): void {
  try {
    _persistedEnrichedUser = user;
    localStorage.setItem(ENRICHED_PROFILE_KEY, JSON.stringify(user));
  } catch (e) {
    console.warn('⚠️ Impossible de persister le profil enrichi:', e);
  }
}

function readEnrichedProfile(): User | null {
  if (_persistedEnrichedUser) return _persistedEnrichedUser;
  try {
    const raw = localStorage.getItem(ENRICHED_PROFILE_KEY);
    if (raw) {
      _persistedEnrichedUser = JSON.parse(raw) as User;
      return _persistedEnrichedUser;
    }
    return null;
  } catch (e) {
    localStorage.removeItem(ENRICHED_PROFILE_KEY);
    return null;
  }
}

/** Réinitialise le cache enrichi (appelé au logout). */
export function resetGlobalEnrichGuard() {
  _persistedEnrichedUser = null;
  localStorage.removeItem(ENRICHED_PROFILE_KEY);
}

// ─── Context type ─────────────────────────────────────────────────────────────

export interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
  usingMockData: boolean;
  updateProfile: (userData: Partial<User>) => Promise<User | null>;
  uploadProfilePhoto: (file: File) => Promise<string | null>;
  refreshUserData: () => void;
  syncProfileAfterLogin: () => Promise<void>;
  resyncAuth0Data: () => Promise<User>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isEnrichingData, setIsEnrichingData] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [usingMockData, setUsingMockData] = useState<boolean>(false);

  // Un seul provider = guards par instance suffisent (plus de guards module-level)
  const enrichInProgressRef = useRef(false);
  const lastEnrichedUserIdRef = useRef<string | undefined>(undefined);
  const retryCountRef = useRef(0);

  const toast = useToast();

  // ── Initialisation depuis les données Auth0 ──────────────────────────────

  const initializeUserFromAuth0 = useCallback((): User | null => {
    const token = getToken();
    const auth0UserData = localStorage.getItem('auth0_user');

    console.log('🔍 initializeUserFromAuth0 - État:', {
      hasToken: !!token,
      hasAuth0Data: !!auth0UserData,
      tokenLength: token?.length || 0,
    });

    const isTokenValid = !!token;
    setIsAuthenticated(isTokenValid);

    if (auth0UserData && isTokenValid) {
      try {
        const auth0User = JSON.parse(auth0UserData);
        const userWithDefaults: User = {
          ...auth0User,
          id: auth0User.sub || auth0User.id || '',
          name: auth0User.name || auth0User.nickname || auth0User.email || 'Utilisateur',
          email: auth0User.email || 'N/A',
          picture:
            auth0User.picture ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(auth0User.name || auth0User.email || 'U')}`,
        };

        // Hydratation immédiate depuis le cache si disponible
        const cached = readEnrichedProfile();
        if (cached) {
          const cachedId = cached.id || cached.email;
          const currentId = userWithDefaults.id || userWithDefaults.email;
          if (cachedId === currentId) {
            const enriched = { ...userWithDefaults, ...cached, picture: userWithDefaults.picture || cached.picture };
            setUser(enriched);
            console.log('⚡ Utilisateur hydraté immédiatement depuis le cache enrichi:', enriched.name);
            return enriched;
          }
        }

        setUser(userWithDefaults);
        console.log('👤 Utilisateur Auth0 initialisé:', userWithDefaults.name || userWithDefaults.email);
        return userWithDefaults;
      } catch (e) {
        console.error('❌ Erreur lors de la lecture des données Auth0 du localStorage', e);
        localStorage.removeItem('auth0_user');
        setUser(null);
        setIsAuthenticated(false);
        return null;
      }
    }

    if (!isTokenValid) {
      setUser(null);
      setError(null);
      retryCountRef.current = 0;
      lastEnrichedUserIdRef.current = undefined;
    }

    return null;
  }, []);

  // ── Enrichissement depuis /users/me ──────────────────────────────────────

  const enrichUserData = useCallback(async (initialUser: User | null) => {
    if (!initialUser) {
      console.log('❌ Pas d\'utilisateur initial pour enrichir les données');
      return;
    }

    // Guard: un seul enrichissement à la fois (par instance = par provider)
    if (enrichInProgressRef.current) {
      console.log('⏸️ Enrichissement déjà en cours, ignoré');
      return;
    }

    const initialUserId = initialUser.id || initialUser.email;
    if (lastEnrichedUserIdRef.current === initialUserId) {
      console.log('⚡ Utilisateur déjà enrichi pour cet ID — ignorer');
      return;
    }

    const token = getToken();
    if (!token) {
      console.log('❌ Pas de token disponible pour enrichir les données');
      return;
    }

    console.log('🚀 Enrichissement des données utilisateur démarré pour:', initialUser.name || initialUser.email);

    setIsEnrichingData(true);
    enrichInProgressRef.current = true;

    try {
      let enrichedUser: User | null = null;

      const isBackendHealthy = await checkBackendHealth();

      if (isBackendHealthy) {
        try {
          console.log('🔍 DEBUG - État auth avant requête API:');
          console.log('- Token présent:', !!token);
          console.log('- Token longueur:', token?.length || 0);
          console.log('- URL Backend:', import.meta.env.VITE_API_URL);
          console.log('- Utilisateur initial:', initialUser.name || initialUser.email);
          console.log('🔍 État d\'authentification vérifié');

          console.log('🌐 Récupération des données utilisateur depuis l\'API');
          let apiUser = await userApiService.getCurrentUser(true);

          console.log('🔑 DEBUG - ID API reçu:', apiUser.id);
          console.log('🔑 DEBUG - ID initial (Auth0):', initialUser.id);
          console.log('📧 DEBUG - Email API:', apiUser.email);
          console.log('👤 DEBUG - Nom API:', apiUser.name);

          // Détection des données temporaires
          const isTemporaryEmail = apiUser.email?.includes('@wanzo.temp') || apiUser.email?.includes('temp-');
          const isTemporaryName = apiUser.name?.startsWith('User-');

          if (isTemporaryEmail || isTemporaryName) {
            console.warn('⚠️ DONNÉES TEMPORAIRES DÉTECTÉES - Resynchronisation avec Auth0...');
            try {
              apiUser = await userApiService.resyncWithAuth0Data();
              console.log('✅ Resynchronisation réussie!');
            } catch (syncError) {
              console.error('❌ Échec de la resynchronisation:', syncError);
              apiUser = {
                ...apiUser,
                email: initialUser.email || apiUser.email,
                name: initialUser.name || apiUser.name,
              };
            }
          }

          enrichedUser = {
            ...initialUser,
            ...apiUser,
            picture: apiUser.picture || initialUser.picture,
          };

          console.log('🔑 DEBUG - ID après fusion:', enrichedUser.id);

          try {
            const refreshedUser = await userApiService.getCurrentUser(true);
            enrichedUser = { ...enrichedUser, ...refreshedUser };
            console.log('🔑 DEBUG - ID après refresh:', enrichedUser.id);
          } catch (fixError) {
            console.warn('⚠️ Correction automatique échouée:', fixError);
          }

          setUsingMockData(false);
          console.log('✅ Données utilisateur récupérées depuis l\'API avec succès');
        } catch (apiError: any) {
          console.log('❌ Erreur lors de la récupération utilisateur:', apiError);

          if (apiError?.status === 401) {
            console.log('🔑 Erreur 401 - Token invalide ou manquant');
            const isFirstTime = sessionStorage.getItem('auth0_first_time_user') === 'true';

            if (isFirstTime) {
              console.log('👤 Premier signup détecté malgré 401, tentative de création...');
              try {
                const freshToken = await ensureFreshToken();
                if (freshToken) {
                  const preferredUserType = getPreferredUserType();
                  console.log('📝 Création avec userType:', preferredUserType);
                  const newUser = await userApiService.createFirstTimeUser({
                    email: initialUser.email,
                    name: initialUser.name || initialUser.email || 'Utilisateur',
                    userType: preferredUserType,
                  });
                  enrichedUser = { ...initialUser, ...newUser, picture: newUser.picture || initialUser.picture };
                  setUsingMockData(false);
                  console.log('✅ Premier utilisateur créé avec succès après refresh token');
                  sessionStorage.removeItem('auth0_first_time_user');
                } else {
                  throw new Error('Impossible de rafraîchir le token');
                }
              } catch (createError) {
                console.warn(`⚠️ Échec création après 401, fallback mocks: ${createError instanceof Error ? createError.message : 'Erreur inconnue'}`);
                const mockUser = await userService.getProfile(true);
                enrichedUser = { ...initialUser, ...mockUser };
                setUsingMockData(true);
              }
            } else {
              console.warn('⚠️ Erreur 401 pour utilisateur existant — tentative de refresh token...');
              try {
                const freshToken = await ensureFreshToken();
                if (freshToken) {
                  try {
                    const retryUser = await userApiService.getCurrentUser(true);
                    enrichedUser = { ...initialUser, ...retryUser };
                    setUsingMockData(false);
                    console.log('✅ Récupération réussie après refresh token');
                  } catch (retryError: any) {
                    const isReallyFirstTime = sessionStorage.getItem('auth0_first_time_user') === 'true';
                    if (isReallyFirstTime) {
                      console.log('🔄 Premier signup confirmé, tentative de synchronisation backend...');
                      try {
                        const preferredUserType = getPreferredUserType();
                        const syncedUser = await userApiService.createFirstTimeUser({
                          email: initialUser.email,
                          name: initialUser.name || initialUser.email || 'Utilisateur',
                          userType: preferredUserType,
                        });
                        enrichedUser = { ...initialUser, ...syncedUser, picture: syncedUser.picture || initialUser.picture };
                        setUsingMockData(false);
                        console.log('✅ Création backend réussie après refresh token');
                        sessionStorage.removeItem('auth0_first_time_user');
                        sessionStorage.removeItem('auth0_just_logged_in');
                      } catch (createError) {
                        console.warn('⚠️ Échec création après refresh, fallback mocks:', createError);
                        const mockUser = await userService.getProfile(true);
                        enrichedUser = { ...initialUser, ...mockUser };
                        setUsingMockData(true);
                      }
                    } else {
                      console.warn('⚠️ 401 persistant — tentative de sync backend avant fallback mocks');
                      try {
                        const preferredUserType = getPreferredUserType();
                        const syncedUser = await userApiService.createFirstTimeUser({
                          email: initialUser.email,
                          name: initialUser.name || initialUser.email || 'Utilisateur',
                          userType: preferredUserType,
                        });
                        enrichedUser = { ...initialUser, ...syncedUser, picture: syncedUser.picture || initialUser.picture };
                        setUsingMockData(false);
                        console.log('✅ Sync backend réussie après 401 persistant');
                      } catch (syncError: any) {
                        console.warn('⚠️ Sync backend échouée, fallback mocks:', syncError?.message || syncError);
                        const mockUser = await userService.getProfile(true);
                        enrichedUser = { ...initialUser, ...mockUser };
                        setUsingMockData(true);
                      }
                    }
                  }
                } else {
                  console.warn('⚠️ Aucun token frais disponible, fallback mocks');
                  const mockUser = await userService.getProfile(true);
                  enrichedUser = { ...initialUser, ...mockUser };
                  setUsingMockData(true);
                }
              } catch (refreshError) {
                console.warn('⚠️ Refresh token échoué, fallback mocks:', refreshError);
                const mockUser = await userService.getProfile(true);
                enrichedUser = { ...initialUser, ...mockUser };
                setUsingMockData(true);
              }
            }
          } else if (apiError?.status === 404) {
            console.log('👤 Utilisateur non trouvé côté backend (404), création du premier utilisateur...');
            try {
              const isFirstTime = sessionStorage.getItem('auth0_first_time_user') === 'true';
              console.log(`📝 Création utilisateur backend (premier utilisateur: ${isFirstTime})`);
              const preferredUserType = getPreferredUserType();
              console.log('📝 Création avec userType:', preferredUserType);
              const newUser = await userApiService.createFirstTimeUser({
                email: initialUser.email,
                name: initialUser.name || initialUser.email || 'Utilisateur',
                userType: preferredUserType,
              });
              enrichedUser = { ...initialUser, ...newUser, picture: newUser.picture || initialUser.picture };
              setUsingMockData(false);
              console.log('✅ Premier utilisateur créé avec succès côté backend');
              sessionStorage.removeItem('auth0_first_time_user');
            } catch (createError) {
              console.warn(`⚠️ Erreur lors de la création du premier utilisateur, utilisation des données mockées: ${createError instanceof Error ? createError.message : 'Erreur inconnue'}`);
              const mockUser = await userService.getProfile(true);
              enrichedUser = { ...initialUser, ...mockUser };
              setUsingMockData(true);
            }
          } else {
            console.warn(`⚠️ Erreur API (${apiError?.status || 'inconnue'}), utilisation des données mockées: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
            const mockUser = await userService.getProfile(true);
            enrichedUser = { ...initialUser, ...mockUser };
            setUsingMockData(true);
          }
        }
      } else {
        console.warn('⚠️ Backend non disponible, utilisation des données mockées');
        const mockUser = await userService.getProfile(true);
        enrichedUser = { ...initialUser, ...mockUser };
        setUsingMockData(true);
      }

      if (enrichedUser) {
        console.log('✨ Données utilisateur enrichies:', enrichedUser.name, '— companyId:', enrichedUser.companyId);
        setUser(enrichedUser);
        lastEnrichedUserIdRef.current = enrichedUser.id || enrichedUser.email;
        persistEnrichedProfile(enrichedUser);
        sessionStorage.removeItem('auth0_just_logged_in');
      }

      setError(null);
      retryCountRef.current = 0;
    } catch (err) {
      const errorObj = err as Error;
      console.error('❌ Erreur lors de l\'enrichissement des données utilisateur:', errorObj);
      setError(errorObj.message);

      const backendStatus = getBackendHealthStatus();
      if (backendStatus === 'healthy' && retryCountRef.current < MAX_RETRIES) {
        const nextRetry = retryCountRef.current + 1;
        console.log(`⏱️ Tentative de récupération ${nextRetry}/${MAX_RETRIES} dans ${RETRY_DELAY / 1000}s...`);
        retryCountRef.current = nextRetry;
        enrichInProgressRef.current = false;
        setTimeout(() => {
          enrichUserData(initialUser);
        }, RETRY_DELAY);
      } else {
        console.error('❌ Nombre maximum de tentatives atteint ou backend indisponible');
        try {
          const mockUser = await userService.getProfile(true);
          const fallbackUser = { ...initialUser, ...mockUser };
          setUser(fallbackUser);
          setUsingMockData(true);
          console.log('🧪 Fallback final sur les données mockées');
        } catch (mockError) {
          console.error('❌ Échec du fallback mockées également:', mockError);
        }
      }
    } finally {
      setIsEnrichingData(false);
      enrichInProgressRef.current = false;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effets ────────────────────────────────────────────────────────────────

  useEffect(() => {
    console.log('🚀 UserProvider - Initialisation au démarrage...');
    const userData = initializeUserFromAuth0();
    if (userData) {
      console.log('👤 Utilisateur initialisé avec données Auth0:', userData.name);
      enrichUserData(userData);
    } else {
      console.log('❌ Aucune donnée Auth0 trouvée au démarrage');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.storageArea === localStorage && (e.key === 'auth0_token' || e.key === 'auth0_user')) {
        console.log('🔄 Changement externe détecté dans le stockage Auth0, rechargement du profil');
        // Reset guard so the new login can be enriched
        lastEnrichedUserIdRef.current = undefined;
        const userData = initializeUserFromAuth0();
        if (userData) {
          enrichUserData(userData);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleAuthChange = () => {
      console.log("🔑 UserProvider - Changement d'état d'authentification détecté");
      // Reset guard so re-enrichment happens on the next auth event
      lastEnrichedUserIdRef.current = undefined;
      const userData = initializeUserFromAuth0();
      if (userData) {
        enrichUserData(userData);
      }
    };
    window.addEventListener(AUTH_EVENT, handleAuthChange);
    return () => window.removeEventListener(AUTH_EVENT, handleAuthChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mise à jour du profil ─────────────────────────────────────────────────

  const updateProfile = useCallback(async (userData: Partial<User>): Promise<User | null> => {
    if (!user) return null;

    setIsUpdating(true);

    try {
      let updatedUser: User;

      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        console.log('📤 Envoi des données utilisateur à l\'API:', userData);
        const apiUser = await userApiService.updateUserProfile(userData);
        updatedUser = { ...user, ...apiUser, picture: apiUser.picture || user.picture };
        console.log('✅ Profil utilisateur mis à jour via l\'API avec succès:', {
          companyId: updatedUser.companyId,
          financialInstitutionId: updatedUser.financialInstitutionId,
        });
        await userService.updateProfile(userData);
      } catch (apiError) {
        console.warn(`⚠️ Impossible de mettre à jour via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        updatedUser = await userService.updateProfile(userData);
        setUsingMockData(true);
      }

      setUser(updatedUser);
      // Mettre à jour le cache enrichi
      persistEnrichedProfile(updatedUser);
      lastEnrichedUserIdRef.current = updatedUser.id || updatedUser.email;
      setError(null);

      toast.showToast?.({
        type: 'success',
        title: 'Profil mis à jour',
        message: 'Vos informations ont été mises à jour avec succès',
      });

      return updatedUser;
    } catch (err) {
      const errorObj = err as Error;
      console.error('❌ Erreur lors de la mise à jour du profil:', errorObj);
      setError(errorObj.message);
      toast.showToast?.({
        type: 'error',
        title: 'Erreur',
        message: `Échec de la mise à jour du profil: ${errorObj.message}`,
      });
      return null;
    } finally {
      setIsUpdating(false);
    }
  }, [user, usingMockData, toast]);

  // ── Upload photo ──────────────────────────────────────────────────────────

  const uploadProfilePhoto = useCallback(async (file: File): Promise<string | null> => {
    if (!user) return null;

    setIsUpdating(true);

    try {
      console.log('📤 Upload de la photo de profil sur Cloudinary...');
      const cloudinaryResult = await cloudinaryService.uploadAvatar(file);
      const photoUrl = cloudinaryResult.url;
      console.log('✅ Photo uploadée sur Cloudinary:', photoUrl);

      try {
        console.log('📤 Mise à jour du profil utilisateur avec l\'URL Cloudinary...');
        // `picture` is not in UpdateUserProfileRequest but the /me PATCH endpoint accepts it
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await userApiService.updateUserProfile({ picture: photoUrl } as any);
        console.log('✅ Profil utilisateur mis à jour via l\'API avec succès');
      } catch (apiError) {
        console.warn(`⚠️ Impossible de mettre à jour le profil via l'API: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        setUsingMockData(true);
      }

      setUser(prev => (prev ? { ...prev, picture: photoUrl } : null));
      setError(null);

      toast.showToast?.({
        type: 'success',
        title: 'Photo mise à jour',
        message: 'Votre photo de profil a été mise à jour avec succès',
      });

      return photoUrl;
    } catch (err) {
      const errorObj = err as Error;
      console.error('❌ Erreur lors de l\'upload de la photo:', errorObj);
      setError(errorObj.message);
      toast.showToast?.({
        type: 'error',
        title: 'Erreur',
        message: `Échec de l'upload de la photo: ${errorObj.message}`,
      });
      return null;
    } finally {
      setIsUpdating(false);
    }
  }, [user, toast]);

  // ── Refresh manuel ────────────────────────────────────────────────────────

  const refreshUserData = useCallback(() => {
    // Réinitialiser le guard pour forcer un nouvel appel à /users/me
    lastEnrichedUserIdRef.current = undefined;
    if (user) {
      enrichUserData(user);
    } else {
      const userData = initializeUserFromAuth0();
      if (userData) {
        enrichUserData(userData);
      }
    }
  }, [user, initializeUserFromAuth0, enrichUserData]);

  // ── Re-sync automatique sur focus de l'onglet ─────────────────────────────
  //
  // Cas d'usage: un transfer-ownership a eu lieu côté accounting-service
  // pendant que l'utilisateur avait Wanzo Land ouvert mais inactif. À son
  // retour sur l'onglet, on re-fetch `/users/me` pour récupérer le rôle/
  // companyId à jour (ou détecter un 401 si la session Auth0 a été invalidée).
  //
  // Throttle 30s : on ne re-fetch pas plus d'une fois toutes les 30 secondes
  // pour ne pas spammer le backend si l'utilisateur switche d'onglet rapidement.
  const lastFocusRefreshRef = useRef<number>(0);
  useEffect(() => {
    const FOCUS_REFRESH_THROTTLE_MS = 30_000;
    const handleFocus = () => {
      const now = Date.now();
      if (now - lastFocusRefreshRef.current < FOCUS_REFRESH_THROTTLE_MS) {
        return; // throttle
      }
      // On ne re-fetch que si l'utilisateur est connecté
      if (!user) return;
      lastFocusRefreshRef.current = now;
      console.log('🔄 Window focus detected — refreshing user data');
      refreshUserData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, refreshUserData]);

  // ── Sync après login ──────────────────────────────────────────────────────

  const syncProfileAfterLogin = useCallback(async () => {
    console.log('🔄 Synchronisation du profil après connexion Auth0');
    lastEnrichedUserIdRef.current = undefined;
    const userData = initializeUserFromAuth0();
    if (userData) {
      await enrichUserData(userData);
    }
  }, [initializeUserFromAuth0, enrichUserData]);

  // ── Resync Auth0 ──────────────────────────────────────────────────────────

  const resyncAuth0Data = useCallback(async (): Promise<User> => {
    try {
      setIsUpdating(true);
      console.log('🔄 Resynchronisation manuelle avec Auth0...');
      const updatedUser = await userApiService.resyncWithAuth0Data();
      setUser(prev => (prev ? { ...prev, ...updatedUser } : updatedUser));
      console.log('✅ Resynchronisation réussie');
      return updatedUser;
    } catch (err) {
      console.error('❌ Échec de la resynchronisation:', err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  // ── Provider value ────────────────────────────────────────────────────────

  const value: UserContextType = {
    user,
    isAuthenticated,
    isLoading: isEnrichingData,
    isUpdating,
    error,
    usingMockData,
    updateProfile,
    uploadProfilePhoto,
    refreshUserData,
    syncProfileAfterLogin,
    resyncAuth0Data,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUserContext(): UserContextType {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUserContext must be used within <UserProvider>');
  return ctx;
}
